import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { scaleFadeIn } from '@helpers/variants';
import MotionTrack from '@stories/Components/Carousel/MotionTrack/MotionTrack';
import SimpleTrackStyles from '@stories/Components/Carousel/MotionTrack/MotionTrack.styles';
import { ButtonStyles } from '@stories/Elements/Forms/Buttons/Button.styles';
import { Typography } from '@stories/Elements/Typography/Typography.styles';
import Containers from '@stories/Settings/Containers/Containers.styles';
import { Grid } from '@stories/Settings/Grids/Grid.styles';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';

const Wrapper = styled.div`
  background-color: ${brand.white};
  --topOffset: 0px;
`;

const FooterDescriptionContainer = styled.div`
  background: ${brand.grey[95]};
  display: flex;
  justify-content: center;
  padding: 30px 0;
`;

const FooterDescriptionInner = styled(Containers.Default)``;

const FooterDescription = styled.p`
  ${Typography.PStyle};
  text-align: center;

  a {
    color: ${brand.red.base};

    &:visited {
      color: ${brand.red.base};
    }
  }
`;

const CompareWrapper = styled(m.div).attrs({
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  variants: scaleFadeIn(1, 0.6),
})<{ 'data-mobile-nav-offset'?: number }>`
  position: fixed;
  width: 100%;
  height: 172px;
  top: ${(props) => props['data-mobile-nav-offset'] ?? 0}px;
  background-color: rgba(0, 0, 0, 80%);
  z-index: 40;
  padding: 22px;
  transition: top 0.15s ease-in-out;

  @media ${until(Device.TabletLarge)} {
    height: 100px;
  }

  ${Grid.Default} {
    height: 100%;

    @media ${until(Device.TabletLarge)} {
      padding: 0;
    }
  }
`;

const CompareItems = styled.div`
  grid-column: 1 / span 10;
  display: flex;
  align-items: center;
  grid-gap: 30px;
  flex-direction: row-reverse;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;

const CompareItem = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 25%;
  height: 100%;
  border: 1px dashed ${brand.white};
  border-radius: 2px;
  padding: 12px;
  position: relative;
`;

const CompareItemInner = styled.div`
  display: flex;
  align-items: center;
  gap: ${fluid(15, 30)};
`;

const CompareItemImageContainer = styled.div`
  width: 84px;
  height: 84px;
  background-color: ${brand.white};
  border-radius: 3px;
  aspect-ratio: 1 / 1;
`;

const CompareItemImage = styled.img``;

const CompareItemTitleContainer = styled.div``;

const CompareModelCode = styled.div`
  font-size: ${fluid(12, 14)};
  ${fonts.helvetica['63-medium-extended']};
  color: ${brand.grey[90]};
  margin-bottom: 6px;
`;

const CompareProductName = styled.div`
  font-size: ${fluid(12, 14)};
  ${fonts.helvetica['73-bold-extended']};
  color: ${brand.white};
  text-transform: uppercase;
`;

const CompareClose = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
    color: ${brand.white};
  }
`;

const CompareButtonContainer = styled.div`
  grid-column: 11 / span 2;
  display: flex;
  flex-direction: column;

  @media ${until(Device.TabletLarge)} {
    grid-column: 1 / span 12;
    flex-direction: row;
    gap: ${fluid(15, 30, Device.Mobile, Device.TabletLarge)};
    justify-content: space-between;
  }
`;

const CompareButtonText = styled.div`
  color: ${brand.white};
  display: flex;
  flex-direction: column;

  @media ${until(Device.TabletLarge)} {
    font-size: 12px;
  }
`;

const CompareClearAll = styled.div`
  ${fonts.helvetica['73-bold-extended']};
  color: ${brand.white};
  font-size: 14px;
  line-height: 22px;
  margin-top: auto;
  text-decoration: underline;
  cursor: pointer;

  @media ${until(Device.TabletLarge)} {
    font-size: 12px;
    line-height: 12px;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;

const CompareButton = styled.div`
  @media ${from(Device.TabletLarge)} {
    margin-top: auto;
  }

  @media ${until(Device.TabletLarge)} {
    ${ButtonStyles.ButtonElement} {
      font-size: 11px;
      width: max-content;
      max-width: 100%;
    }
  }
`;

const CompareOverlay = styled(m.div).attrs({
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  variants: scaleFadeIn(1, 0.6),
})`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1001;
`;

const CategoryFiltersWrapper = styled(Grid.Default)`
  margin-bottom: 16px;
`;

const CategoryFilters = styled.div`
  grid-gap: 8px;
  margin-bottom: 16px;
  --visibleItems: 3.4;
  --itemGutter: 20px;

  @media ${from(Device.Tablet)} {
    --visibleItems: 5;
  }

  @media ${from(Device.TabletLarge)} {
    --visibleItems: 6;
  }

  @media ${from(Device.DesktopSmall)} {
    --itemGutter: 8px;
    --visibleItems: 10;
  }
`;

const Track = styled(MotionTrack)`
  overflow: hidden;
  margin: 0 calc((var(--itemGutter) / 2) * -1) 30px;

  &[data-center='false'] {
    ${() => SimpleTrackStyles.Track} {
      justify-content: center;
    }
  }
`;

const CategoryFilterItemWrapper = styled.li`
  flex: 0 0 calc(100% / var(--visibleItems));
  padding: 0 calc(var(--itemGutter) / 2);
  cursor: pointer;
`;

const CategoryFilterItem = styled.a`
  display: flex;
  flex-direction: column;
  grid-gap: 10px;
  align-items: center;
  pointer-events: none;
`;

const CategoryFilterItemImageContainer = styled.div`
  width: 80px;
  height: 80px;
  aspect-ratio: 1 / 1;
  border-radius: 4px;
  border: 1px solid ${brand.grey[90]};

  @media ${from(Device.TabletLarge)} {
    width: 120px;
    height: 120px;
  }
`;
const CategoryFilterItemImage = styled.img``;

const CategoryFilterItemTitle = styled.div`
  font-size: 14px;
  ${fonts.helvetica['73-bold-extended']};
  text-align: center;
  color: ${brand.black};
  line-height: normal;

  @media ${until(Device.TabletLarge)} {
    ${fonts.helvetica['55-roman']};
  }
`;

const ControlsContainer = styled.div``;

const LineContainer = styled.div<{ progress: number }>`
  position: relative;

  &:before {
    content: '';
    width: 100%;
    height: 1px;
    background-color: ${brand.grey[70]};
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  &:after {
    content: '';
    height: 6px;
    background-color: ${brand.red.base};
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    transition: width 0.5s ease-in-out;

    ${({ progress }) =>
      css`
        width: ${progress}%;
      `}
  }
`;

const Controls = styled.div`
  display: flex;
  grid-gap: 8px;
  justify-content: flex-end;
  padding-top: 12px;
`;

export const ProductListingStyles = {
  Wrapper,
  FooterDescriptionContainer,
  FooterDescriptionInner,
  FooterDescription,
  CompareWrapper,
  CompareItems,
  CompareItem,
  CompareItemInner,
  CompareItemImageContainer,
  CompareItemImage,
  CompareItemTitleContainer,
  CompareModelCode,
  CompareProductName,
  CompareClose,
  CompareButtonContainer,
  CompareButtonText,
  CompareClearAll,
  CompareButton,
  CompareOverlay,
  CategoryFiltersWrapper,
  CategoryFilters,
  Track,
  CategoryFilterItemWrapper,
  CategoryFilterItem,
  CategoryFilterItemImageContainer,
  CategoryFilterItemImage,
  CategoryFilterItemTitle,
  ControlsContainer,
  LineContainer,
  Controls,
};
